import React, { useState, useEffect, useRef, useMemo } from 'react';
import { db, auth } from '../firebase';
import { supabase } from '../supabase';
import { collection, addDoc, getDocs, deleteDoc, doc, query, orderBy, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import ReactQuill, { Quill } from 'react-quill-new';
import ImageResize from 'quill-image-resize-module-react';
import { LayoutDashboard, Newspaper, Users, UserCheck, FileText, Mail, Settings, TrendingUp, Check, X, Edit3, Globe, PieChart, UsersRound } from 'lucide-react';
import 'react-quill-new/dist/quill.snow.css';
import './AdminDashboard.css';

Quill.register('modules/imageResize', ImageResize);

interface Article { id: string; titulo: string; autor: string; data: any; imagens?: string[]; }
interface SiteSettings { 
  heroTitle: string; heroDescription: string; footerDescription: string; contactEmail: string; contactLocation: string; 
  registrationOpen: boolean; registrationDeadline: string; countIntegrantes: number; countPesquisasProduzidas: number; countPesquisasAndamento: number; countInstituicoes: number;
  useAutoPesquisas?: boolean;
}
interface Participant { 
  id: string; 
  name: string; 
  email: string; 
  academicLevel: string; 
  country: string; 
  attendanceMode: string; 
  trabalhoUrl?: string;
  participatingAs?: string;
  linhaPrincipal?: string;
  linhasAdicionais?: string[];
  temaTrabalho?: string;
  situacaoTrabalho?: string;
  asistencia: { dia1: boolean; dia2: boolean; dia3: boolean }; 
}
interface EventTokens { dia1?: { token: string; expiresAt: string }; dia2?: { token: string; expiresAt: string }; dia3?: { token: string; expiresAt: string }; }
interface GiepeiDoc { id: string; titulo: string; url: string; data: any; size?: number; }
interface PortalUser { id: string; email: string; displayName: string; lastAccess: any; }

const AdminDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'new' | 'list' | 'settings' | 'participants' | 'integrantes' | 'messages' | 'docs' | 'users'>('dashboard');
  const [articles, setArticles] = useState<Article[]>([]);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [portalUsers, setPortalUsers] = useState<PortalUser[]>([]);
  const [members, setMembers] = useState<any[]>([]);
  const [messages, setMessages] = useState<any[]>([]);
  const [docsList, setDocsList] = useState<GiepeiDoc[]>([]);
  const [eventTokens, setEventTokens] = useState<EventTokens>({});
  const [adminsCount, setAdminsCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const [settings, setSettings] = useState<SiteSettings>({
    heroTitle: '', heroDescription: '', footerDescription: '', contactEmail: '', contactLocation: '', registrationOpen: true, registrationDeadline: '', countIntegrantes: 0, countPesquisasProduzidas: 0, countPesquisasAndamento: 0, countInstituicoes: 0
  });

  const [memberForm, setMemberForm] = useState({ id: '', nome: '', email: '', filiacao: '', localizacao: '' });
  const [isEditingMember, setIsEditingMember] = useState(false);
  const [docTitulo, setDocTitulo] = useState('');
  const [docFile, setDocFile] = useState<File | null>(null);
  const [titulo, setTitulo] = useState('');
  const [autor, setAutor] = useState('');
  const [conteudo, setConteudo] = useState('');

  const navigate = useNavigate();
  const quillRef = useRef<ReactQuill>(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const qSnapArt = await getDocs(query(collection(db, 'articles'), orderBy('data', 'desc')));
      setArticles(qSnapArt.docs.map(doc => ({ id: doc.id, ...doc.data() } as Article)));
      
      const qSnapPart = await getDocs(collection(db, 'participantes'));
      const partsList = qSnapPart.docs.map(doc => ({ id: doc.id, ...doc.data() } as Participant));
      setParticipants(partsList);

      const qSnapPortalUsers = await getDocs(query(collection(db, 'usuarios_portal'), orderBy('lastAccess', 'desc')));
      setPortalUsers(qSnapPortalUsers.docs.map(doc => ({ id: doc.id, ...doc.data() } as PortalUser)));

      const qSnapMembers = await getDocs(query(collection(db, 'integrantes_giepei'), orderBy('nome', 'asc')));
      setMembers(qSnapMembers.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      
      const qSnapMsgs = await getDocs(query(collection(db, 'mensagens_contacto'), orderBy('data', 'desc')));
      setMessages(qSnapMsgs.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      
      const qSnapDocs = await getDocs(query(collection(db, 'documentos_giepei'), orderBy('data', 'desc')));
      setDocsList(qSnapDocs.docs.map(doc => ({ id: doc.id, ...doc.data() } as GiepeiDoc)));
      
      const qSnapAdmins = await getDocs(collection(db, 'admins'));
      setAdminsCount(qSnapAdmins.size);
      
      const settingsSnap = await getDoc(doc(db, 'settings', 'main'));
      if (settingsSnap.exists()) {
        const data = settingsSnap.data();
        
        // Calcular de forma automática a partir de los presentadores en la lista de participantes
        const autoProduzidas = partsList.filter(p => p.participatingAs === 'Apresentador' && p.situacaoTrabalho === 'Concluído').length;
        const autoAndamento = partsList.filter(p => p.participatingAs === 'Apresentador' && p.situacaoTrabalho === 'Em Andamento').length;

        const useAuto = data.useAutoPesquisas !== false; // por defecto true
        const finalProduzidas = useAuto ? autoProduzidas : (data.countPesquisasProduzidas || 0);
        const finalAndamento = useAuto ? autoAndamento : (data.countPesquisasAndamento || 0);

        // Si cambia el conteo en modo automático, actualizamos en segundo plano
        if (useAuto && (data.countPesquisasProduzidas !== autoProduzidas || data.countPesquisasAndamento !== autoAndamento)) {
          await setDoc(doc(db, 'settings', 'main'), {
            countPesquisasProduzidas: autoProduzidas,
            countPesquisasAndamento: autoAndamento,
            useAutoPesquisas: true
          }, { merge: true });
        }

        setSettings({
          heroTitle: data.heroTitle || '',
          heroDescription: data.heroDescription || '',
          footerDescription: data.footerDescription || '',
          contactEmail: data.contactEmail || '',
          contactLocation: data.contactLocation || '',
          registrationOpen: data.registrationOpen !== false,
          registrationDeadline: data.registrationDeadline || '',
          countIntegrantes: data.countIntegrantes || 0,
          countPesquisasProduzidas: finalProduzidas,
          countPesquisasAndamento: finalAndamento,
          countInstituicoes: data.countInstituicoes || 0,
          useAutoPesquisas: useAuto
        });
      }
      const tokensSnap = await getDoc(doc(db, 'config_evento', 'tokens'));
      if (tokensSnap.exists()) setEventTokens(tokensSnap.data() as EventTokens);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const stats = useMemo(() => {
    const countries = participants.reduce((acc: any, p) => {
      const c = p.country || 'N/A';
      acc[c] = (acc[c] || 0) + 1;
      return acc;
    }, {});
    const sortedCountries = Object.entries(countries).sort(([, a]: any, [, b]: any) => b - a).slice(0, 5);
    const presencial = participants.filter(p => p.attendanceMode === 'Presencial').length;
    const online = participants.filter(p => p.attendanceMode === 'Online').length;
    return { sortedCountries, presencial, online };
  }, [participants]);

  const handleSaveMember = async (e: React.FormEvent) => {
    e.preventDefault(); setLoading(true);
    try {
      if (isEditingMember) await updateDoc(doc(db, 'integrantes_giepei', memberForm.id), { ...memberForm });
      else await addDoc(collection(db, 'integrantes_giepei'), { ...memberForm });
      setMemberForm({ id: '', nome: '', email: '', filiacao: '', localizacao: '' });
      setIsEditingMember(false); fetchData();
    } catch (err) { alert('Erro!'); }
    setLoading(false);
  };

  const clearMembers = async () => {
    if (!window.confirm('Apagar todos os integrantes?')) return;
    setLoading(true);
    try {
      const qSnap = await getDocs(collection(db, 'integrantes_giepei'));
      for (const d of qSnap.docs) await deleteDoc(doc(db, 'integrantes_giepei', d.id));
      fetchData();
    } catch (err) { alert('Erro!'); }
    setLoading(false);
  };

  const migrateMembersFromCSV = async () => {
    if (!window.confirm('Importar os 59 integrantes?')) return;
    setLoading(true);
    try {
      const csvData = [
        ["Adriana Ferreira Daffre", "adrianadaffre@gmail.com", "Secretaria Municipal de Educação da Cidade de São Paulo", "São Paulo - Brasil"],
        ["Alberto Gola", "alberto.gola@espbengo.edu.ao", "Escola Superior Pedagógica do Bengo - ESPE-Bengo", "Bengo - Angola"],
        ["Alberto Kapitango Nguluve", "alberluve@gmail.com", "Centro de Investigação e Desenvolvimento do ISCED de Luanda.", "Luanda - Angola"],
        ["Alice Inocêncio", "aliceinocencio17@gmail.com", "Instituto Superior de Ciências de Educação ISCED da Huíla.", "Huíla - Angola"],
        ["Ana Yeca", "anamarcelinaana84@gmail.com", "Instituto Superior de Ciências de Educação ISCED do Uíge", "Uíge - Angola"],
        ["Andrea Moruzzi", "andreamoruzzi@ufscar.br", "Universidade Federal de São Carlos - UFSCar.", "São Paulo - Brasil"],
        ["Anildo Jorge Tóto", "anildooto@gmail.com", "Instituto Superior de Ciências de Educação ISCED do Uíge", "Uíge - Angola"],
        ["Antanagilda Calima", "antanagildapinto@gmail.com", "Gabinete Provincial da Educação do Bengo.", "Bengo - Angola"],
        ["António Esteves Tomás", "antoniotomas2703@gmail.com", "Instituto Superior de Ciências de Educação ISCED do Uíge", "Uíge - Angola"],
        ["António Nóvoa", "", "Universidade de Lisboa.", "Lisboa - Portugal"],
        ["Augusto Moura Rasga", "augustomourarasga@gmail.com", "Instituto Superior de Ciências de Educação ISCED da Huíla.", "Huíla - Angola"],
        ["Befilia Jumba António Jacob", "befiliajacobjacob@gmail.com", "Instituto Superior de Ciências de Educação ISCED do Bié.", "Bié - Angola"],
        ["Betacuro Miguel André Teca", "betancuroteca@gmail.com", "Instituto Superior Politécnico Privado do Uíge ISPPU.", "Uíge - Angola"],
        ["Briand Francisco Boloy Belett", "2bbcubano@gmail.com", "Instituto Superior Politécnico Privado do Uíge ISPPU.", "Uíge - Angola"],
        ["Claudia Oliveira Fernandes", "claudiaofa@gmail.com", "Secretaria de Educação da Cidade de São Paulo. / Universidad Federal de São Carlos (UFSCar).", "São Paulo - Brasil"],
        ["Claudio Oliveira Fernandes", "claudioof@gmail.com", "Secretária de Educação do Estado de São Paulo e Secretária Municipal da Cidade de São Paulo", "São Paulo - Brasil"],
        ["Cleonice Maria Tomazzetti", "cmtomazzetti@ufscar.br", "Universidade Federal de São Carlos (UFSCar)", "São Paulo - Brasil"],
        ["Cristiano Virgílio Augusto Filipe", "uige1989@gmail.com", "Instituto Superior Politécnico Privado do Uíge ISPPU.", "Uíge - Angola"],
        ["Cristina Gomes da Silva", "csilvasolid@gmail.com", "Escola Superior de Educação del Instituto Politécnico de Setúbal-Portugal", "Setúbal - Portugal"],
        ["Eliza Campos Manuel Salvador", "Elisacampos2210@gmail.com", "Gabinete Provincial de Educação do Bengo", "Bengo - Angola"],
        ["Evaristo Vitangui Gando", "evaristovitangui@gmail.com", "Instituto Superior de Ciências de Educação ISCED do Bié.", "Bié - Angola"],
        ["Florença Tchingolo Baião Daniel", "florenca.daniel@isced-huila.edu.ao", "Instituto Superior de Ciências de Educação ISCED da Huíla.", "Huíla Lubango - Angola"],
        ["Francisco Bizingi", "fbizingi@gmail.com", "Instituto Superior Politécnico Privado do Uíge ISPPU.", "Uíge - Angola"],
        ["Gabriela Tebet", "gabigt@unicamp.br", "Universidade Estadual de Campinas UNICAMP", "São Paulo - Brasil"],
        ["Gabriella Pizzolante da Silva", "gabi.pizzolante@ufscar.br", "Universidade Federal de São Carlos (UFSCar) - campus São Carlos", "São Carlos - SP - Brasil"],
        ["Garcia Manuel Cabando", "garciakabando1992@gmail.com", "Escola Superior Pedagógica do Bengo - ESPE-Bengo", "Bengo - Angola"],
        ["Gercelina Jacinto", "gercelina2@gmail.com", "Escola Superior Pedagógica do Bengo - ESPE-Bengo", "Bengo - Angola"],
        ["Gilberta Valentina Savimbo Martins", "gilberta.martins@espbengo.edu.ao", "Escola Superior Pedagógica do Bengo - ESPE-Bengo", "Bengo - Angola"],
        ["Guilherme do Val Toledo Prado", "toledo@unicamp.br", "Universidade Estadual de Campinas UNICAMP", "Campinas/São Paulo/Brasil"],
        ["Heloísa A. Matos Lins", "hmlins@unicamp.br", "Universidade Estadual de Campinas UNICAMP", "São Paulo/Brasil"],
        ["Hilário Piriquito Eurico", "hilariopeurico@gmail.com", "Escola Superior Pedagógica do Bengo - ESPE-Bengo", "Bengo - Angola"],
        ["Jaqueline da Conceição Camargo", "jaqueline.conceicao@estudante.ufscar.br", "Universidade Federal de São Carlos (UFSCar) / Secretaria Municipal de Educación de Campinas", "São Paulo/ Brasil"],
        ["Jorge Manuel de Sousa Chaves", "jorgemchaves2000@yahoo.com.br", "Instituto Superior de Ciências de Educação ISCED da Huíla.", "Huila - Angola"],
        ["Ketiene Moreira da Silva", "ketienesilva@gmail.com", "Universidade de São Paulo, Universidade Federal de São Carlos (UFSCar)", "São Paulo - Brasil"],
        ["Laura Rodrigues Paim Pamplona", "laura.pamplona@estudante.ufscar.br", "Instituto Federal de Educação, Ciência e Tecnologia do Sul de Minas Gerais IFSULDEMINAS / Universidad Federal de São Carlos (UFSCar)", "Minas Gerais/Brasil"],
        ["Lavínia L. S. Magiolino", "laviniam@unicamp.br", "Universidade Estadual de Campinas UNICAMP", "Campinas, São Paulo, Brasil"],
        ["Luzia Pedro Samba", "luaziapedrosamba@gmail.com", "Instituto Superior Politécnico Privado do Uíge ISPPU.", "Uíge - Angola"],
        ["Magalys Dihigo Custtiss", "magalysdihigo08@gmail.com", "Instituto Superior Politécnico Kalandula ISPEKA. Instituto Superior Politécnico Katangoji ISPK", "Luanda - Angola"],
        ["Manuel Fernando", "kurizema@yahoo.com.br", "Gabinete Provincial da Educação do Bengo", "Bengo - Angola"],
        ["Manuel Saliulo", "mcsoa4@iscte-iul.pt", "Gabinete Provincial da Educación do Bengo", "Bengo - Angola"],
        ["Márcia Lúcia Anacleto de Souza", "anacleto@unicamp.br", "Prefeitura Municipal de Campinas", "São Paulo/Brasil"],
        ["Maria Antónia Custódio Ngalayessanda", "Ngalayessanda21@gmail.com", "Ministério del Ensino Superior Ciência, Tecnología e Inovaçao MESCTI", "Luanda - Angola"],
        ["Maria João Francisco Chipalavela", "mariachipalavelamj@gmail.com", "Instituto Superior de Ciências de Educação ISCED da Huíla.", "Huíla - Angola"],
        ["Maria Walburga dos Santos", "", "Universidade Federal de São Carlos (UFSCar)", "Sorocaba - SP - Brasil"],
        ["Mayra Silva dos Santos", "Mayra.silva9152@gmail.com", "Universidade Federal de Minas Gerais UFMG", "Belo Horizonte - Minas Gerais - Brasil"],
        ["Narciso Jorge Chimbolo Dumbo", "narcisodumbo@hotmail.com", "Instituto Superior de Ciências de Educação ISCED de Benguela", "Benguela - Angola"],
        ["Nayara Gisele Borges Ferreira da Silva", "", "Universidade Estadual de Campinas UNICAMP", "Campinas - São Paulo - Brasil"],
        ["Nicolau Sorte Lucas", "nicolaulucas81@gmail.com", "Instituto Superior Politécnico Privado do Uíge ISPPU.", "Uíge - Angola"],
        ["Paulino Soma Adriano", "paulino.adriano@isced-huila.edu.ao", "Instituto Superior de Ciências de Educação ISCED da Huíla / Universidade Católica de Angola UCAN", "Huíla - Angola"],
        ["Paulo Fabrício Lira Roquete", "pauloroquette@gmail.com", "Universidade Estadual de Campinas UNICAMP", "Vinhedo-SP - Brasil"],
        ["Rosana Olimpia Belett Guilarte", "rosanabelett52@gmail.com", "Instituto Superior Politécnico Privado do Uíge ISPPU.", "Uíge - Angola"],
        ["Sabina Júlia Capolonga Adriano", "sbnadriano@gmail.com", "Instituto Superior de Ciências de Educación ISCED da Huíla.", "Huíla Lubango - Angola"],
        ["Sinhá Carvalho de Oliveira", "sinha.oliveira@yahoo.com.br", "Secretaria Municipal de Educação de São Paulo / Secretaria da Educação del Estado de São Paulo", "São Paulo - Brasil"],
        ["Teresa Manuela C. J. da Costa", "Teresa-da-costa@hotmail.com", "Escola Superior Pedagógica do Bengo - ESPE-Bengo", "Bengo - Angola"],
        ["Thaís Gonçalves", "thays-goncalves@hotmail.com", "Universidade Estadual de Campinas UNICAMP", "Araras - SP - Brasil"],
        ["Vanessa Medianeira da Silva Flôres", "vanessaf.educ@gmail.com", "Prefeitura Municipal de Piraquara / Universidade Federal del Paraná UFPR", "Piraquara - Paraná - Brasil"],
        ["Victor João Morales", "Victor.morales@espbengo.edu.ao", "Escola Superior Pedagógica do Bengo - ESPE-Bengo", "Bengo - Angola"],
        ["Vicente Ricardo Pedro Nguinamau", "vicente928563078@gmail.com", "Instituto Superior Politécnico Privado do Uíge ISPPU.", "Uíge - Angola"],
        ["Yusdalmis Anache Calunga Gando", "calungaanache@gmail.com", "Instituto Superior de Ciências de Educação ISCED do Bié.", "Bié - Angola"]
      ];
      for (const item of csvData) { await addDoc(collection(db, 'integrantes_giepei'), { nome: item[0], email: item[1], filiacao: item[2], localizacao: item[3] }); }
      fetchData(); alert('Importado!');
    } catch (err) { alert('Erro!'); }
    setLoading(false);
  };

  const handleUploadDoc = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!docFile || !docTitulo) return;
    setLoading(true);
    try {
      const fileName = `${Date.now()}_${docFile.name}`;
      const { error: uploadError } = await supabase.storage.from('files').upload(`pdfs/${fileName}`, docFile);
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from('files').getPublicUrl(`pdfs/${fileName}`);
      await addDoc(collection(db, 'documentos_giepei'), { titulo: docTitulo, url: publicUrl, data: new Date(), size: docFile.size });
      setDocTitulo(''); setDocFile(null); fetchData(); alert('Documento publicado!');
    } catch (err) { alert('Erro!'); }
    setLoading(false);
  };

  const handleDeleteDoc = async (id: string, url: string) => {
    if (!window.confirm('Excluir?')) return;
    try {
      const fileName = url.split('/').pop();
      if (fileName) await supabase.storage.from('files').remove([`pdfs/${fileName}`]);
      await deleteDoc(doc(db, 'documentos_giepei', id));
      fetchData();
    } catch (err) { alert('Erro!'); }
  };

  const resetSystemData = async () => {
    if (!window.confirm("Nuclear reset?")) return;
    setLoading(true);
    try {
      const pSnap = await getDocs(collection(db, 'participantes'));
      for (const d of pSnap.docs) await deleteDoc(doc(db, 'participantes', d.id));
      const mSnap = await getDocs(collection(db, 'mensagens_contacto'));
      for (const d of mSnap.docs) await deleteDoc(doc(db, 'mensagens_contacto', d.id));
      await setDoc(doc(db, 'settings', 'registration_stats'), { presencialCount: 0 }, { merge: true });
      fetchData(); alert('Reset concluído!');
    } catch (err) { alert('Erro!'); }
    setLoading(false);
  };

  const generateToken = async (dia: string) => {
    const newToken = Math.random().toString(36).substring(2, 15);
    const expiresAt = new Date(Date.now() + 6 * 60 * 60 * 1000).toISOString();
    const updated = { ...eventTokens, [dia]: { token: newToken, expiresAt } };
    setEventTokens(updated);
    await setDoc(doc(db, 'config_evento', 'tokens'), updated);
  };

  const exportPresencaList = () => {
    const presencialParticipants = participants.filter(p => p.attendanceMode === 'Presencial');
    if (presencialParticipants.length === 0) {
      alert('Nenhum participante presencial encontrado.');
      return;
    }
    let content = 'LISTA DE PRESENÇA - 1.º Seminário GIEPEI\n';
    content += 'Dias: 13, 14 e 15 de Julho de 2026\n\n';
    content += presencialParticipants.map((p, i) => `${i + 1}. ${p.name}\n\n________________________________________\n\n`).join('\n');
    content += `\nTotal: ${presencialParticipants.length} participante(s) presencial(is).`;

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Lista_Presenca_GIEPEI_${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const modules = useMemo(() => ({
    toolbar: [['undo', 'redo'], [{ 'header': [1, 2, 3, false] }], ['bold', 'italic', 'underline'], [{ 'list': 'ordered'}, { 'list': 'bullet' }], ['link', 'clean']],
    imageResize: { parchment: { whitelist: ['width', 'height', 'style'] } }
  }), []);

  return (
    <div className="admin-dashboard container">
      <div className="admin-header"><h2>GIEPEI Central Control</h2><button onClick={async () => { await signOut(auth); navigate('/'); }} className="btn-logout">Sair</button></div>

      <nav className="admin-tabs">
        <button className={activeTab === 'dashboard' ? 'active' : ''} onClick={() => setActiveTab('dashboard')}><LayoutDashboard size={18} /> Dashboard</button>
        <button className={activeTab === 'users' ? 'active' : ''} onClick={() => setActiveTab('users')}><UsersRound size={18} /> Usuários</button>
        <button className={activeTab === 'participants' ? 'active' : ''} onClick={() => setActiveTab('participants')}><UserCheck size={18} /> Inscrições</button>
        <button className={activeTab === 'integrantes' ? 'active' : ''} onClick={() => setActiveTab('integrantes')}><Users size={18} /> Integrantes</button>
        <button className={activeTab === 'docs' ? 'active' : ''} onClick={() => setActiveTab('docs')}><FileText size={18} /> Documentos</button>
        <button className={activeTab === 'list' || activeTab === 'new' ? 'active' : ''} onClick={() => setActiveTab('list')}><Newspaper size={18} /> Artigos</button>
        <button className={activeTab === 'messages' ? 'active' : ''} onClick={() => setActiveTab('messages')}><Mail size={18} /> Mensagens</button>
        <button className={activeTab === 'settings' ? 'active' : ''} onClick={() => setActiveTab('settings')}><Settings size={18} /> Configuração</button>
      </nav>

      <div className="admin-content">
        {activeTab === 'dashboard' && (
          <div className="dashboard-grid-pro">
            <div className="main-stats">
              <div className="pro-stat-card"><span className="label">Utilizadores Registados</span><span className="value">{portalUsers.length}</span></div>
              <div className="pro-stat-card"><span className="label">Participantes do Evento</span><span className="value">{participants.length}</span></div>
              <div className="pro-stat-card"><span className="label">Membros do Grupo</span><span className="value">{members.length}</span></div>
              <div className="pro-stat-card"><span className="label">Total de Artigos</span><span className="value">{articles.length}</span></div>
            </div>
            <div className="charts-grid-pro">
              <div className="pro-chart-card">
                <h3>Modalidades de Inscrição</h3>
                <div className="horizontal-bar-container">
                  <div className="bar-row">
                    <span className="bar-label">Presencial ({stats.presencial})</span>
                    <div className="bar-track"><div className="bar-fill" style={{ width: `${(stats.presencial/participants.length)*100 || 0}%`, background: '#5B341B' }}></div></div>
                  </div>
                  <div className="bar-row">
                    <span className="bar-label">Online ({stats.online})</span>
                    <div className="bar-track"><div className="bar-fill" style={{ width: `${(stats.online/participants.length)*100 || 0}%`, background: '#8C5E3C' }}></div></div>
                  </div>
                </div>
              </div>
              <div className="pro-chart-card">
                <h3>Países (Top 5)</h3>
                <div className="top-list-pro">
                  {stats.sortedCountries.map(([name, count]: any, idx) => (
                    <div key={name} className="list-item-pro">
                      <span className="rank">#{idx+1}</span>
                      <span className="name">{name}</span>
                      <span className="count">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="pro-chart-card">
                <h3>Visão de Impacto</h3>
                <div className="impact-stats-pro">
                  <div className="impact-item"><span>Pesquisas Produzidas</span><strong>{settings.countPesquisasProduzidas}</strong></div>
                  <div className="impact-item"><span>Documentos Publicados</span><strong>{docsList.length}</strong></div>
                  <div className="impact-item"><span>Administradores ativos</span><strong>{adminsCount}</strong></div>
                  <div className="impact-item"><span>Instituições Parceiras</span><strong>{settings.countInstituicoes}</strong></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <section className="admin-card">
            <h3>Utilizadores do Portal</h3>
            <p style={{ marginBottom: '1.5rem', color: '#666', fontSize: '0.9rem' }}>Todos os utilizadores que criaram conta ou entraram via Google (independentemente da inscrição no evento).</p>
            <div className="table-responsive">
              <table className="admin-table">
                <thead><tr><th>Nome</th><th>E-mail</th><th>Último Acesso</th></tr></thead>
                <tbody>{portalUsers.map(u => (
                  <tr key={u.id}>
                    <td>{u.displayName || 'N/A'}</td>
                    <td>{u.email}</td>
                    <td>{u.lastAccess?.seconds ? new Date(u.lastAccess.seconds * 1000).toLocaleString('pt-BR') : 'Recentemente'}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </section>
        )}

        {/* Mantendo as outras abas... */}
        {activeTab === 'participants' && (
          <section className="admin-card">
            <div className="card-header-actions"><h3>Inscrições</h3><div className="status-toggle"><span>Abertas:</span><label className="switch"><input type="checkbox" checked={settings.registrationOpen} onChange={async (e) => { const ns = e.target.checked; setSettings({...settings, registrationOpen: ns}); await updateDoc(doc(db, 'settings', 'main'), { registrationOpen: ns }); }} /><span className="slider"></span></label></div><button onClick={exportPresencaList} className="btn btn-secondary" style={{marginLeft: '1rem'}}>Lista de Presença</button></div>
            <div className="deadline-config-box"><h4>Prazo Limite</h4><div className="deadline-form-flex"><input type="datetime-local" value={settings.registrationDeadline?.substring(0, 16)} onChange={e => setSettings({...settings, registrationDeadline: new Date(e.target.value).toISOString()})} /><button onClick={async () => { await updateDoc(doc(db, 'settings', 'main'), { registrationDeadline: settings.registrationDeadline }); alert('Salvo!'); }} className="btn btn-secondary">Salvar</button></div></div>
            <div className="token-generator-section"><h4>Links de Presença</h4><div className="token-grid-responsive">{['dia1', 'dia2', 'dia3'].map(dia => (<div key={dia} className="token-card"><h5>{dia.toUpperCase()}</h5><button onClick={() => generateToken(dia)} className="btn-small">Gerar Link</button>{eventTokens[dia as keyof EventTokens] && (<div className="token-info"><input readOnly value={`${window.location.origin}/checkin/${dia}?token=${eventTokens[dia as keyof EventTokens]?.token}`} /></div>)}</div>))}</div></div>
            <div className="table-responsive">
              <table className="admin-table">
                <thead><tr><th>Nome</th><th>E-mail</th><th>País</th><th>Modo</th><th>Trabalho</th></tr></thead>
                <tbody>{participants.map(p => (
                  <tr key={p.id}>
                    <td>{p.name}</td>
                    <td>{p.email}</td>
                    <td>{p.country}</td>
                    <td>{p.attendanceMode}</td>
                    <td>
                      {p.trabalhoUrl ? (
                        <a href={p.trabalhoUrl} target="_blank" rel="noopener noreferrer" className="btn-small" style={{ background: '#2e7d32', textDecoration: 'none' }}>Ver PDF/Doc</a>
                      ) : <span style={{ color: '#ccc', fontSize: '0.8rem' }}>N/A</span>}
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </section>
        )}

        {activeTab === 'integrantes' && (
          <section className="admin-card">
            <div className="card-header-actions"><h3>Membros do Grupo</h3><div className="header-btns"><button onClick={migrateMembersFromCSV} className="btn btn-secondary btn-small">Importar</button><button onClick={clearMembers} className="btn-delete btn-small">Limpar</button></div></div>
            <form onSubmit={handleSaveMember} className="admin-form">
              <div className="form-grid-responsive">
                <div className="form-group"><label>Nome</label><input value={memberForm.nome} onChange={e => setMemberForm({ ...memberForm, nome: e.target.value })} required /></div>
                <div className="form-group"><label>Email</label><input type="email" value={memberForm.email} onChange={e => setMemberForm({ ...memberForm, email: e.target.value })} required /></div>
                <div className="form-group"><label>Filiação</label><input value={memberForm.filiacao} onChange={e => setMemberForm({ ...memberForm, filiacao: e.target.value })} required /></div>
                <div className="form-group"><label>Localização</label><input value={memberForm.localizacao} onChange={e => setMemberForm({ ...memberForm, localizacao: e.target.value })} required /></div>
              </div>
              <button type="submit" className="btn btn-primary">{isEditingMember ? 'Atualizar' : 'Adicionar'}</button>
            </form>
            <div className="table-responsive"><table className="admin-table"><thead><tr><th>Nome</th><th>Email</th><th>Ações</th></tr></thead><tbody>{members.map(m => (<tr key={m.id}><td>{m.nome}</td><td>{m.email}</td><td><div style={{display:'flex', gap:'0.5rem'}}><button onClick={() => { setMemberForm(m); setIsEditingMember(true); window.scrollTo(0,0); }} className="btn-small">Editar</button><button onClick={async () => { if(window.confirm('Excluir?')){ await deleteDoc(doc(db, 'integrantes_giepei', m.id)); fetchData(); } }} className="btn-delete">Excluir</button></div></td></tr>))}</tbody></table></div>
          </section>
        )}

        {activeTab === 'docs' && (
          <section className="admin-card">
            <h3>Documentos PDF</h3>
            <form onSubmit={handleUploadDoc} className="admin-form doc-management-form">
              <div className="form-grid-responsive">
                <div className="form-group"><label>Título</label><input value={docTitulo} onChange={e => setDocTitulo(e.target.value)} required /></div>
                <div className="form-group"><label>PDF</label><input type="file" accept=".pdf" onChange={e => setDocFile(e.target.files?.[0] || null)} required /></div>
                <div className="form-group btn-align-end"><button type="submit" className="btn btn-primary">Publicar</button></div>
              </div>
            </form>
            <div className="table-responsive"><table className="admin-table"><thead><tr><th>Título</th><th>Data</th><th>Ações</th></tr></thead><tbody>{docsList.map(d => (<tr key={d.id}><td>{d.titulo}</td><td>{new Date(d.data?.seconds * 1000).toLocaleDateString()}</td><td><div style={{display:'flex', gap:'0.5rem'}}><a href={d.url} target="_blank" rel="noopener noreferrer" className="btn-small">Ver</a><button onClick={() => handleDeleteDoc(d.id, d.url)} className="btn-delete">Excluir</button></div></td></tr>))}</tbody></table></div>
          </section>
        )}

        {activeTab === 'messages' && (
          <section className="admin-card">
            <h3>Mensagens</h3>
            <div className="table-responsive"><table className="admin-table"><thead><tr><th>Remetente</th><th>Assunto</th><th>Ações</th></tr></thead><tbody>{messages.map(msg => (<tr key={msg.id}><td>{msg.nome} ({msg.email})</td><td>{msg.assunto}</td><td><button onClick={async () => { if(window.confirm('Excluir?')){ await deleteDoc(doc(db, 'mensagens_contacto', msg.id)); fetchData(); } }} className="btn-delete">Excluir</button></td></tr>))}</tbody></table></div>
          </section>
        )}

        {activeTab === 'new' && <section className="admin-card"><div className="card-header-actions"><h3>Novo Artigo</h3><button onClick={() => setActiveTab('list')} className="btn btn-secondary btn-small">Voltar para a Lista</button></div><form onSubmit={async (e) => { e.preventDefault(); setLoading(true); try { await addDoc(collection(db, 'articles'), { titulo, autor, conteudo, data: new Date() }); setTitulo(''); setAutor(''); setConteudo(''); fetchData(); alert('Publicado!'); } catch (err) { alert('Erro!'); } setLoading(false); }}><div className="form-group"><label>Título</label><input value={titulo} onChange={e => setTitulo(e.target.value)} required /></div><div className="form-group"><label>Autor</label><input value={autor} onChange={e => setAutor(e.target.value)} required /></div><div className="form-group editor-group"><ReactQuill ref={quillRef} theme="snow" value={conteudo} onChange={setConteudo} modules={{toolbar: [['bold', 'italic', 'underline'], [{ 'list': 'ordered'}, { 'list': 'bullet' }], ['link', 'clean']]}} /></div><button type="submit" className="btn btn-primary">Publicar</button></form></section>}
        {activeTab === 'list' && <section className="admin-card"><div className="card-header-actions"><h3>Artigos</h3><button onClick={() => setActiveTab('new')} className="btn btn-primary btn-small">Escrever Artigo</button></div><div className="table-responsive"><table className="admin-table"><thead><tr><th>Título</th><th>Autor</th><th>Ações</th></tr></thead><tbody>{articles.map(article => (<tr key={article.id}><td>{article.titulo}</td><td>{article.autor}</td><td><button onClick={async () => { if (window.confirm('Excluir?')) { await deleteDoc(doc(db, 'articles', article.id)); fetchData(); } }} className="btn-delete">Excluir</button></td></tr>))}</tbody></table></div></section>}
        {activeTab === 'settings' && <section className="admin-card">
          <h3>Configurações Globais</h3>
          <form onSubmit={async (e) => { e.preventDefault(); await setDoc(doc(db, 'settings', 'main'), settings); alert('Salvo!'); }} className="admin-form">
            <div className="form-grid-responsive">
              <div className="form-group"><label>Título Principal (Hero)</label><input value={settings.heroTitle} onChange={e => setSettings({...settings, heroTitle: e.target.value})} /></div>
              <div className="form-group"><label>Email Oficial</label><input type="email" value={settings.contactEmail} onChange={e => setSettings({...settings, contactEmail: e.target.value})} /></div>
            </div>
            <div className="form-group"><label>Descrição do Grupo</label><textarea value={settings.heroDescription} onChange={e => setSettings({...settings, heroDescription: e.target.value})} rows={3} /></div>
             <div style={{gridColumn: 'span 2', padding: '0.2rem 0', display: 'flex', gap: '0.5rem', alignItems: 'center'}}>
              <input type="checkbox" id="useAutoPesquisas" checked={settings.useAutoPesquisas !== false} onChange={e => {
                const isChecked = e.target.checked;
                let countProduzidas = settings.countPesquisasProduzidas;
                let countAndamento = settings.countPesquisasAndamento;
                if (isChecked) {
                  countProduzidas = participants.filter(p => p.participatingAs === 'Apresentador' && p.situacaoTrabalho === 'Concluído').length;
                  countAndamento = participants.filter(p => p.participatingAs === 'Apresentador' && p.situacaoTrabalho === 'Em Andamento').length;
                }
                setSettings({
                  ...settings,
                  useAutoPesquisas: isChecked,
                  countPesquisasProduzidas: countProduzidas,
                  countPesquisasAndamento: countAndamento
                });
              }} />
              <label htmlFor="useAutoPesquisas" style={{cursor: 'pointer', userSelect: 'none', fontWeight: 500, fontSize: '0.9rem', color: 'var(--text-dark)'}}>Calcular pesquisas de forma automática a partir dos inscritos apresentadores</label>
            </div>
            <div className="form-row-grid" style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1.5rem'}}>
              <div className="form-group"><label>Localização Display</label><input value={settings.contactLocation} onChange={e => setSettings({...settings, contactLocation: e.target.value})} /></div>
              <div className="form-group">
                <label>Pesquisas Produzidas</label>
                <input type="number" disabled={settings.useAutoPesquisas !== false} value={settings.countPesquisasProduzidas} onChange={e => setSettings({...settings, countPesquisasProduzidas: parseInt(e.target.value) || 0})} />
                {settings.useAutoPesquisas !== false && <span style={{fontSize: '0.7rem', color: '#888', display: 'block', marginTop: '0.2rem'}}>Valor calculado automaticamente. Desmarque a opção para alterar manualmente.</span>}
              </div>
            </div>
            <div className="form-row-grid" style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1.5rem', marginTop:'1rem'}}>
              <div className="form-group">
                <label>Pesquisas em Andamento</label>
                <input type="number" disabled={settings.useAutoPesquisas !== false} value={settings.countPesquisasAndamento} onChange={e => setSettings({...settings, countPesquisasAndamento: parseInt(e.target.value) || 0})} />
                {settings.useAutoPesquisas !== false && <span style={{fontSize: '0.7rem', color: '#888', display: 'block', marginTop: '0.2rem'}}>Valor calculado automaticamente. Desmarque a opção para alterar manualmente.</span>}
              </div>
              <div className="form-group"><label>Instituições Parceiras</label><input type="number" value={settings.countInstituicoes} onChange={e => setSettings({...settings, countInstituicoes: parseInt(e.target.value) || 0})} /></div>
            </div>
            <button type="submit" className="btn btn-primary btn-form-submit">Atualizar Configurações</button>
          </form>
          <div className="danger-zone-box"><h4>Zona de Perigo</h4><button onClick={resetSystemData} className="btn-delete">Resetar Dados de Teste</button></div>
        </section>}
      </div>
    </div>
  );
};

export default AdminDashboard;
